import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { getAuth, updatePassword } from 'firebase/auth';
import { DialogService } from 'primeng/dynamicdialog';
import { Subscription } from 'rxjs';
import { HINT_PASSWORD_LENGTH, SUCCESS } from 'src/app/global';
import { ERROR_PASSWORD_INVALID_LENGTH, ERROR_PASSWORD_MATCH, ERROR_PASSWORD_MISC, KEY_CODE_RETURN } from 'src/app/calvin-emr-shared/global';
import { ToastService } from 'src/app/calvin-emr-shared/services/toast.service';
import { LoginDialogComponent } from 'src/app/shared/login-dialog/login-dialog.component';
import { PATH_SETTINGS_PROFILE } from '../settings.global';
import { CardModule } from 'primeng/card';
import { CalvinEMRSharedModule } from '../../calvin-emr-shared/calvin-emr-shared.module';
import { NgIf } from '@angular/common';
import { ButtonDirective } from 'primeng/button';

@Component({
  selector: 'app-change-password-page',
  templateUrl: './change-password-page.component.html',
  styleUrls: ['./change-password-page.component.scss'],
  providers: [DialogService],
  standalone: true,
  imports: [
    CardModule,
    CalvinEMRSharedModule,
    NgIf,
    ButtonDirective,
  ],
})
export class ChangePasswordPageComponent implements OnInit, OnDestroy {
  /**
   * Local constant to store the value of global variable 'passwordLengthHint'.
   */
  passwordLengthHint = HINT_PASSWORD_LENGTH;

  /**
   * Form that contains the controls for the password inputs.
   */
  passwordForm = this.fb.group({
    password: [''],
    retypedPassword: [''],
  });

  /**
   * Flag to determine whether to hide the first password input's text.
   */
  hidePassword = true;

  /**
   * Flag to determine whether to hide the second password input's text.
   */
  hideRetypedPassword = true;

  /**
   * Subscription to the form to track any changes made to the form's controls.
   */
  passwordSub: Subscription;

  /**
   * Error message to display on the page.
   */
  error = '';

  didSubmit = false;

  /**
   * Subscribes to the form to set error to blank whenever the form's value changes;
   * this gets rid of any error messages when the user starts typing.
   *
   * @param fb FormBuilder class.
   * @param router Router to use when routing user after password has been changed.
   */
  constructor(
    private fb: UntypedFormBuilder,
    private router: Router,
    public dialogService: DialogService,
    private toastService: ToastService
  ) {
    this.passwordSub = this.passwordForm.valueChanges.subscribe(() => {
      this.error = '';
    });
  }

  /**
   * Subscribes to the dialog close action. If the dialog has returned false,
   * the router navigates back to the 'settings' page.
   */
  ngOnInit(): void {
    const dialogRef = this.dialogService.open(LoginDialogComponent, {
      header: 'Verify Identity',
    });

    dialogRef.onClose.subscribe((authenticated: boolean) => {
      if (!authenticated) {
        this.router.navigateByUrl(PATH_SETTINGS_PROFILE);
      }
    });
  }

  /**
   * Unsubscribe from all active subscriptions.
   */
  ngOnDestroy(): void {
    this.passwordSub.unsubscribe();
  }

  /**
   * Calls the onSubmit function when the "Enter/Return" key is pressed.
   *
   * @param event Object passed by (keyup) property of input tag.
   */
  handleKeyUp(event: any) {
    if (event.keyCode == KEY_CODE_RETURN) {
      this.onSubmit();
    }
  }

  /**
   * Handler for when password form is submitted. Checks for and displays any error
   * messages. If no errors, updates the currently signed-in user's password and
   * navigates back to the patient-list page.
   */
  onSubmit() {
    // If password is valid, update it with new value
    if (this.passwordsMatch && this.isValidLength) {
      this.didSubmit = true;

      // Get current user information
      const auth = getAuth();
      const user = auth.currentUser;

      if (user) {
        updatePassword(user, this.password.value)
          .then(() => {
            // Update successful
            this.router.navigateByUrl(PATH_SETTINGS_PROFILE);
          })
          .catch((error) => {
            this.didSubmit = false;

            this.error = error.message;
          })
          .finally(() => {
            this.didSubmit = false;
            this.toastService.clearAndAddOne({
              severity: SUCCESS,
              summary: 'Success',
              detail: 'Updated password!',
            });
          });
      }
    }
    // If password is not valid, display any error messages
    else {
      this.error = this.getError();
    }
  }

  /**
   * Returns various error messages to be displayed on client side.
   * The error messages are checked below in descending precedence.
   */
  getError(): string {
    if (!this.isValidLength) {
      return ERROR_PASSWORD_INVALID_LENGTH;
    }
    if (!this.passwordsMatch) {
      return ERROR_PASSWORD_MATCH;
    }
    return ERROR_PASSWORD_MISC;
  }

  get password(): UntypedFormControl {
    return this.passwordForm.controls.password as UntypedFormControl;
  }

  get retypedPassword(): UntypedFormControl {
    return this.passwordForm.controls.retypedPassword as UntypedFormControl;
  }

  get passwordsMatch() {
    return this.password.value === this.retypedPassword.value;
  }

  get isValidLength() {
    const pwdLength = (this.password.value as string).length;
    return pwdLength >= 6 && pwdLength <= 32;
  }

  /**
   * Determines whether to show error message.
   */
  get hasError(): boolean {
    return this.error != null && this.error != '';
  }
}
